import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useFetchWithBody, usePost } from '../../api/reactQueryCustomHooks';
import {
    Box, Button, Grid, Paper, Autocomplete,
    TableHead, TableRow, TextField, Typography, FormControl, InputLabel,
    Select, MenuItem, IconButton, InputAdornment, Popover, useMediaQuery, Chip, Alert, AlertTitle, ListSubheader,
    CircularProgress
} from '@mui/material';

import { format, toDate } from 'date-fns';
import { apiRoute, ENUM_MOD_NAME, ENUM_MOD_PARAMETER, getModMandatory } from '../../constants/enum';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faDownload, faDownLong, faFilter, faInfo, faRefresh, faSearch } from '@fortawesome/free-solid-svg-icons';
import { ErrorCard, LoadingCard, MainContainer, MUIDataTable, NoDataCard } from '../../components';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from "dayjs"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { head } from 'lodash';
import { toast } from 'react-toastify';

export const ALL_COLUMNS = {

    Chq_Date: { label: "Date", group: "date", type: "date" },
    YearMonth: { label: "Month", group: "month", type: "string" },
    AC_DESC: { label: "Account", group: "account", type: "string" },
    PAc_Desc: { label: "Party", group: "party", type: "string" },
    BankName: { label: "Bank", group: "none", type: "string" },
    Chq_No: { label: "Cheque No", group: "none", type: "string" },
    Chq_Amt: { label: "Amount", group: "none", type: "number" },
    Remark: { label: "Remark", group: "none", type: "string" },


};

export const getColumns = (groupBy, activeTab, useMktAsMfg = true) => {
    return Object.entries(ALL_COLUMNS)
        .filter(([key, col]) => {
            // Handle SL_Code
            const bothgroup = ['Chq_Amt']
            if (bothgroup.includes(key))
                return groupBy === "none" || groupBy === "party" || groupBy === "account" || groupBy === "date" || groupBy === "month";
            if (key == "Chq_Date")
                return groupBy === "none" || groupBy === "date";
            if (key == "AC_DESC")
                return groupBy === "none" || groupBy === "account";
            if (key == "PAc_Desc")
                return groupBy === "none" || groupBy === "party";


            // Handle grouped columns
            if (col.group && col.group !== groupBy) return false;


            return true;
        })
        .map(([key, col]) => ({ key, ...col }));
};
;


const ReceivePaymentReport = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { compCode, compName, startDate, endDate } = useSelector((state) => state.company);
    const tableRef = useRef(null);
    const [groupBy, setGroupBy] = useState('none');
    const [isUseMfgNoAsMktno, setIsUseMfgNoAsMktno] = useState(true)
    const [columnFilters, setColumnFilters] = useState({
        Chq_Date: '',
        YearMonth: '',
        AC_DESC: '',
        PAc_Desc: '',
        BankName: '',
        Chq_No: '',
        Chq_Amt: '',
        Remark: '',
    });

    const [filters, setFilters] = useState({
        compCode: compCode,
        dy: '1',
        fromDate: startDate,//new Date(new Date().setDate(new Date().getDate() - 30)),
        toDate: endDate,// new Date(),
        Vou_Type: 'R',
        AC_ID: ''
    });
    const exportColumnKeys = [
        "Chq_Date",
        "AC_DESC",
        "PAc_Desc",
        "BankName",
        "Chq_No",
        "Chq_Amt",
        "Remark", "YearMonth"
    ];

    const { isError: isErrorModSet, isLoading, data: modSetting = [] } = useFetchWithBody({
        url: apiRoute.modSetting.getModSetting,
        queryKey: "fetchModSettingInwardItem",
        inputData: {
            mod_name: ENUM_MOD_NAME.GeneralSettings
        }
    })
    useEffect(() => {
        if (modSetting && modSetting.length > 0) {
            const mktSetting = getModMandatory(ENUM_MOD_PARAMETER.GeneralSetting.UseMfgNoAsMktNo, modSetting);
            setIsUseMfgNoAsMktno(mktSetting)
        }
    }, [modSetting])

    const { data: accounts = [] } = useFetchWithBody({
        url: apiRoute.transactionEntry.getParties,
        queryKey: ['paymentReportAccounts'],
        inputData: { compCode }
    });



    const {
        data: reportData = [],
        isPending,
        isError,
        error,
        mutate: fetchReportData
    } = usePost({
        url: apiRoute.transactionEntry.getReportData,
        queryKey: ['paymentReport'],
    });



    // // Apply column filters to the data
    const filteredData = useMemo(() => {
        if (!reportData.length) return [];
        return reportData.filter(row => {
            return Object.entries(columnFilters).every(([key, value]) => {
                if (!value) return true;
                const rowValue = row[key] || '';
                return String(rowValue).toLowerCase().includes(String(value).toLowerCase());
            });
        });
    }, [reportData, columnFilters]);




    const filteredAndGroupedData = useMemo(() => {
        if (!reportData?.length) return [];

        // ---------- FILTER ----------
        // const filteredData = reportData.filter(row =>
        //     Object.entries(columnFilters).every(([key, value]) => {
        //         if (!value) return true;
        //         return String(row[key] ?? '')
        //             .toLowerCase()
        //             .includes(String(value).toLowerCase());
        //     })
        // );

        if (groupBy === 'none') return filteredData;

        /* ================= GROUPING ================= */

        const groupedData = {};


        const getGroupKey = (row) => {
            switch (groupBy) {
                case 'account':
                    return row.AC_DESC || 'Account';

                case 'party':
                    return row.PAc_Desc || 'Party';

                case 'date':
                    return format(new Date(row.Chq_Date), 'yyyy-MM-dd');

                case 'month':
                    return row.YearMonth || 'Unknown Month';

                default:
                    return 'all';
            }
        };

        const initGroup = (row, key) => ({
            groupKey: key,
            Chq_Date: groupBy === 'date'
                ? format(new Date(row.Chq_Date), 'dd-MMM-yyyy')
                : groupBy === 'month'
                    ? format(new Date(row.Chq_Date), 'MMMM yyyy')
                    : key,
            AC_DESC: groupBy === 'account' ? row.AC_DESC : key,
            PAc_Desc: groupBy === 'party' ? row.PAc_Desc : key,
            BankName: groupBy === 'bank' ? row.BankName : key,
            YearMonth: groupBy === 'month' ? row.YearMonth : key,
            Chq_No: 0,
            Chq_Amt: 0,
            Remark: "",

            // weighted helpers
            //  _rateWeightedSum: 0,
            //  _weightSum: 0,

            _children: []
        });

        filteredData.forEach(row => {
            const key = getGroupKey(row);

            if (!groupedData[key]) {
                groupedData[key] = initGroup(row, key);
            }

            const group = groupedData[key];

            const add = (field) => {
                group[field] += Number(row[field]) || 0;
            };

            add('Chq_Amt');


            // Example weighted logic (if needed)
            // if (row.Rate && row.Weight) {
            //     group._rateWeightedSum += Number(row.Rate) * Number(row.Weight);
            //     group._weightSum += Number(row.Weight);
            // }

            group._children.push(row);
        });

        /* ================= FINALIZE ================= */

        return Object.values(groupedData)
            .map(({ _rateWeightedSum, _weightSum, ...rest }) => ({
                ...rest,
                Rate: _weightSum ? _rateWeightedSum / _weightSum : 0
            }))
            .sort((a, b) =>
                String(a.groupKey).localeCompare(String(b.groupKey))
            );

    }, [
        filteredData,
        columnFilters,
        filters,
        groupBy
    ]);





    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };




    const handleSubmit = (e) => {
        e.preventDefault();
        if ((filters.fromDate && filters.fromDate == 'Invalid Date')
            || (filters.toDate && filters.toDate == 'Invalid Date')) {
            toast.error("Invalid From Date / To Date", {
                "position": "top-center"
            })
        }
        else {
            const params = {
                ...filters,
                fromDate: format(filters.fromDate, 'yyyy-MM-dd'),
                toDate: format(filters.toDate, 'yyyy-MM-dd'),
                AC_ID: filters.AC_ID || ''
            };
            fetchReportData({ data: params });
        }

        // refetch();
    };

    const handleReset = () => {
        setFilters({
            compCode: compCode.join(","),
            dy: '1',
            fromDate: startDate,// new Date(new Date().setDate(new Date().getDate() - 30)),
            toDate: endDate,// new Date(),
            Vou_Type: 'R'
        });
        setGroupBy("none")
        const params = {
            compCode: compCode.join(","),
            dy: '1',
            Vou_Type: 'R',
            fromDate: startDate,
            toDate: endDate,
            AC_ID: filters.AC_ID || ''
        };
        fetchReportData({ data: params });
    };



    // const calculateTotals = () => {
    //     const totals = {
    //         invAmt: 0,
    //         TdsAmt: 0,
    //         PayAmt: 0,
    //         retAmt: 0,
    //         BalanceAmt: 0
    //     };

    //     filteredData.forEach(row => {
    //         totals.invAmt += parseFloat(row.invAmt) || 0;
    //         totals.TdsAmt += parseFloat(row.TdsAmt) || 0;
    //         totals.PayAmt += parseFloat(row.PayAmt) || 0;
    //         totals.retAmt += parseFloat(row.retAmt) || 0;
    //         totals.BalanceAmt += parseFloat(row.BalanceAmt) || 0;
    //     });

    //     return totals;
    // };


    // const totals = calculateTotals();

    const getReportTitle = () => {
        return filters.Vou_Type === 'R' ? 'Receive Register' : 'Payment Register';
    };


    const headCssStyle = { fontSize: "0.7rem", lineHeight: 1.3, textTransform: "uppercase", backgroundColor: '#f0f0f0', fontWeight: 600, color: '#3c3c3c' }
    const columns = useMemo(() => getColumns(groupBy, "all", isUseMfgNoAsMktno), [isUseMfgNoAsMktno, groupBy]);
    if (isError) {
        return <ErrorCard />
    }
    if (isLoading) {
        return <LoadingCard />
    }
    return (
        <MainContainer
            pageName={`Receive / Payment List`}
            breadcrumbs={["Reports", "Receive / Payment List"]}

        >
            <Box sx={{
                p: isMobile ? 1 : 3,
                backgroundColor: 'background.default',
                minHeight: '100vh',

            }}>
                {/* Header Section */}

                <Box
                    sx={{
                        position: "relative",

                        display: "flex",

                        flexDirection: {
                            xs: "column",
                            sm: "row",
                        },

                        justifyContent: "space-between",

                        alignItems: {
                            xs: "flex-start",
                            sm: "center",
                        },

                        gap: {
                            xs: 1,
                            sm: 1.5,
                        },

                        mb: 1.2,

                        px: {
                            xs: 1.3,
                            sm: 1.7,
                        },

                        py: {
                            xs: 0.9,
                            sm: 1,
                        },

                        minHeight: 44,

                        overflow: "hidden",

                        // Soft professional background
                        background:
                            "#FFFFFF",

                        border:
                            "1px solid #dbeafe",

                        borderRadius: "9px",

                        boxShadow:
                            "0 2px 7px rgba(15, 23, 42, 0.06)",

                        transition:
                            "all 0.2s ease",

                        "&:hover": {
                            borderColor: "#bfdbfe",

                            boxShadow:
                                "0 3px 10px rgba(37, 99, 235, 0.08)",
                        },

                        // Left blue accent
                        "&::before": {
                            content: '""',

                            position: "absolute",

                            left: 0,
                            top: 0,
                            bottom: 0,

                            width: "4px",

                            background:
                                "linear-gradient(180deg, #2563eb, #60a5fa)",
                        },
                    }}
                >
                    {/* =====================================
        REPORT TITLE
    ====================================== */}

                    <Box
                        sx={{
                            display: "flex",

                            alignItems: "center",

                            minWidth: 0,

                            pl: 0.5,
                        }}
                    >
                        {/* Small indicator */}
                        <Box
                            sx={{
                                width: 7,
                                height: 7,

                                flexShrink: 0,

                                mr: 1,

                                borderRadius: "50%",

                                backgroundColor:
                                    "#2563eb",

                                boxShadow:
                                    "0 0 0 3px rgba(37, 99, 235, 0.10)",
                            }}
                        />

                        <Typography
                            variant="h4"
                            sx={{
                                minWidth: 0,

                                fontSize: {
                                    xs: "0.95rem",
                                    sm: "1.05rem",
                                    md: "1.15rem",
                                },

                                fontWeight: 600,

                                color: "#1e40af",

                                letterSpacing: "-0.25px",

                                lineHeight: 1.3,

                                whiteSpace: {
                                    xs: "normal",
                                    sm: "nowrap",
                                },

                                overflow: "hidden",

                                textOverflow: "ellipsis",
                            }}
                        >
                            {getReportTitle()}
                        </Typography>
                    </Box>

                    {/* =====================================
        ACTION AREA
    ====================================== */}

                    {reportData.length > 0 && (
                        <Box
                            sx={{
                                display: "flex",

                                alignItems: "center",

                                justifyContent: {
                                    xs: "flex-start",
                                    sm: "flex-end",
                                },

                                flexWrap: "wrap",

                                gap: 0.8,

                                width: {
                                    xs: "100%",
                                    sm: "auto",
                                },

                                flexShrink: 0,

                                // Style any buttons placed here
                                "& .MuiButton-root": {
                                    height: 32,

                                    minHeight: 32,

                                    minWidth: 80,

                                    px: 1.3,

                                    borderRadius: "7px",

                                    fontSize: "12px",

                                    fontWeight: 600,

                                    textTransform: "none",

                                    boxShadow: "none",

                                    transition:
                                        "all 0.2s ease",

                                    "&:hover": {
                                        transform:
                                            "translateY(-1px)",

                                        boxShadow:
                                            "0 2px 6px rgba(15, 23, 42, 0.10)",
                                    },
                                },

                                "& .MuiButton-startIcon": {
                                    marginRight: "5px",

                                    "& svg": {
                                        fontSize: "12px",
                                    },
                                },

                                // Mobile
                                "@media (max-width:600px)": {
                                    "& .MuiButton-root": {
                                        flex: "1 1 auto",
                                    },
                                },
                            }}
                        >
                            {/* Put your report action buttons here */}
                        </Box>
                    )}
                </Box>



                {/* Filters Section */}
                <div className='p-5'>
                    <form onSubmit={handleSubmit}>

                        <Grid
                            container
                            spacing={1.5}
                            alignItems="center"
                            sx={{
                                mb: 1.5,

                                p: {
                                    xs: 1.2,
                                    sm: 1.5,
                                },

                                borderRadius: "10px",

                                background:
                                    "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",

                                border: "1px solid #e2e8f0",

                                boxShadow:
                                    "0 2px 8px rgba(15, 23, 42, 0.05)",

                                // ==========================================
                                // FORM CONTROL
                                // ==========================================
                                "& .MuiFormControl-root": {
                                    width: "100%",
                                },

                                "& .MuiOutlinedInput-root": {
                                    height: 38,

                                    borderRadius: "7px",

                                    backgroundColor: "#ffffff",

                                    fontSize: "13px",

                                    transition: "all 0.2s ease",

                                    "& fieldset": {
                                        borderColor: "#cbd5e1",

                                        transition:
                                            "border-color 0.2s ease, box-shadow 0.2s ease",
                                    },

                                    "&:hover fieldset": {
                                        borderColor: "#94a3b8",
                                    },

                                    "&.Mui-focused fieldset": {
                                        borderColor: "#2563eb",

                                        borderWidth: "1.5px",

                                        boxShadow:
                                            "0 0 0 2px rgba(37, 99, 235, 0.08)",
                                    },

                                    "&.Mui-disabled": {
                                        backgroundColor: "#f1f5f9",
                                    },
                                },

                                // ==========================================
                                // LABEL
                                // ==========================================
                                "& .MuiInputLabel-root": {
                                    fontSize: "14px",

                                    fontWeight: 500,

                                    color: "#475569",
                                },

                                "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#2563eb",

                                    fontWeight: 600,
                                },

                                // ==========================================
                                // SELECT
                                // ==========================================
                                "& .MuiSelect-select": {
                                    display: "flex",

                                    alignItems: "center",

                                    minHeight:
                                        "38px !important",

                                    boxSizing: "border-box",

                                    paddingTop:
                                        "0 !important",

                                    paddingBottom:
                                        "0 !important",

                                    fontSize: "13px",

                                    color: "#334155",
                                },

                                // ==========================================
                                // DATE PICKER
                                // ==========================================
                                "& .MuiPickersInputBase-root": {
                                    height: 38,

                                    borderRadius: "7px",

                                    backgroundColor: "#ffffff",

                                    fontSize: "13px",
                                },

                                "& .MuiPickersOutlinedInput-notchedOutline": {
                                    borderColor: "#cbd5e1",

                                    transition:
                                        "border-color 0.2s ease, box-shadow 0.2s ease",
                                },

                                "& .MuiPickersInputBase-root:hover .MuiPickersOutlinedInput-notchedOutline":
                                {
                                    borderColor: "#94a3b8",
                                },

                                "& .MuiPickersInputBase-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline":
                                {
                                    borderColor: "#2563eb",

                                    borderWidth: "1.5px",
                                },

                                // ==========================================
                                // DATE INPUT
                                // ==========================================
                                "& .MuiPickersInputBase-root input": {
                                    fontSize: "13px",

                                    color: "#334155",
                                },
                            }}
                        >
                            {/* =================================================
        HIDDEN FIELDS
    ================================================= */}

                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                                sx={{ display: "none" }}
                            >
                                <TextField
                                    fullWidth
                                    label="Company Code"
                                    name="compCode"
                                    value={filters.compCode}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                                sx={{ display: "none" }}
                            >
                                <TextField
                                    fullWidth
                                    label="DY"
                                    name="dy"
                                    value={filters.dy}
                                />
                            </Grid>

                            {/* =================================================
        VOUCHER TYPE
    ================================================= */}

                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                                lg={2}
                            >
                                <FormControl
                                    fullWidth
                                    size="small"
                                >
                                    <InputLabel>
                                        Voucher Type
                                    </InputLabel>

                                    <Select
                                        name="Vou_Type"
                                        value={filters.Vou_Type}
                                        onChange={handleFilterChange}
                                        label="Voucher Type"
                                        sx={{
                                            backgroundColor:
                                                "rgba(37, 99, 235, 0.04)",

                                            "&:hover": {
                                                backgroundColor:
                                                    "rgba(37, 99, 235, 0.07)",
                                            },
                                        }}
                                    >
                                        <MenuItem value="R">
                                            Receive Register
                                        </MenuItem>

                                        <MenuItem value="G">
                                            Payment Register
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* =================================================
        ACCOUNT
    ================================================= */}

                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                                lg={2}
                            >
                                <FormControl
                                    fullWidth
                                    size="small"
                                >
                                    <InputLabel>
                                        Account
                                    </InputLabel>

                                    <Select
                                        name="AC_ID"
                                        value={filters.AC_ID}
                                        onChange={handleFilterChange}
                                        label="Account"
                                    >
                                        <MenuItem value="">
                                            All Accounts
                                        </MenuItem>

                                        {accounts.map(
                                            (account) => (
                                                <MenuItem
                                                    key={
                                                        account.AC_ID
                                                    }
                                                    value={
                                                        account.AC_ID
                                                    }
                                                >
                                                    {
                                                        account.AC_DESC
                                                    }
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* =================================================
        FROM DATE
    ================================================= */}

                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                                lg={2}
                            >
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                >
                                    <DatePicker
                                        label="From Date"
                                        views={[
                                            "day",
                                            "month",
                                            "year",
                                        ]}
                                        format="DD/MM/YYYY"
                                        value={
                                            filters.fromDate
                                                ? dayjs(
                                                    filters.fromDate
                                                )
                                                : null
                                        }
                                        onChange={(newValue) => {
                                            setFilters(
                                                (prev) => ({
                                                    ...prev,

                                                    fromDate:
                                                        newValue &&
                                                            dayjs(
                                                                newValue
                                                            ).isValid()
                                                            ? dayjs(
                                                                newValue
                                                            ).format(
                                                                "YYYY-MM-DD"
                                                            )
                                                            : null,
                                                })
                                            );
                                        }}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                fullWidth: true,

                                                InputLabelProps: {
                                                    sx: {
                                                        fontSize:
                                                            "14px",
                                                        fontWeight: 500,
                                                    },
                                                },
                                            },

                                            popper: {
                                                placement:
                                                    "bottom-start",

                                                disablePortal:
                                                    false,

                                                sx: {
                                                    zIndex: 99999,
                                                },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            {/* =================================================
        TO DATE
    ================================================= */}

                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                                lg={2}
                            >
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                >
                                    <DatePicker
                                        label="To Date"
                                        views={[
                                            "day",
                                            "month",
                                            "year",
                                        ]}
                                        format="DD/MM/YYYY"
                                        value={
                                            filters.toDate
                                                ? dayjs(
                                                    filters.toDate
                                                )
                                                : null
                                        }
                                        onChange={(newValue) => {
                                            setFilters(
                                                (prev) => ({
                                                    ...prev,

                                                    toDate:
                                                        newValue &&
                                                            dayjs(
                                                                newValue
                                                            ).isValid()
                                                            ? dayjs(
                                                                newValue
                                                            ).format(
                                                                "YYYY-MM-DD"
                                                            )
                                                            : null,
                                                })
                                            );
                                        }}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                fullWidth: true,

                                                InputLabelProps: {
                                                    sx: {
                                                        fontSize:
                                                            "14px",
                                                        fontWeight: 500,
                                                    },
                                                },
                                            },

                                            popper: {
                                                placement:
                                                    "bottom-start",

                                                disablePortal:
                                                    false,

                                                sx: {
                                                    zIndex: 99999,
                                                },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            {/* =================================================
        GROUP BY
    ================================================= */}

                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                                lg={2}
                            >
                                <FormControl
                                    fullWidth
                                    size="small"
                                >
                                    <InputLabel>
                                        Group By
                                    </InputLabel>

                                    <Select
                                        label="Group By"
                                        value={groupBy}
                                        onChange={(e) =>
                                            setGroupBy(
                                                e.target.value
                                            )
                                        }
                                    >
                                        <MenuItem value="none">
                                            Report Detail
                                        </MenuItem>

                                        <MenuItem value="account">
                                            Account
                                        </MenuItem>

                                        <MenuItem value="party">
                                            Party
                                        </MenuItem>

                                        <MenuItem value="date">
                                            Date
                                        </MenuItem>

                                        <MenuItem value="month">
                                            Month
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* =================================================
        ACTION BUTTONS
    ================================================= */}

                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                                lg={2}
                            >
                                <Box
                                    sx={{
                                        display: "flex",

                                        alignItems: "center",

                                        justifyContent: {
                                            xs: "flex-start",
                                            sm: "flex-end",
                                        },

                                        gap: 0.8,

                                        width: "100%",
                                    }}
                                >
                                    {/* ==========================
                SHOW BUTTON
            =========================== */}

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="small"
                                        disabled={isPending}
                                        startIcon={
                                            isPending ? (
                                                <CircularProgress
                                                    size={14}
                                                    color="inherit"
                                                />
                                            ) : (
                                                <FontAwesomeIcon
                                                    icon={faSearch}
                                                    style={{
                                                        fontSize:
                                                            "11px",
                                                    }}
                                                />
                                            )
                                        }
                                        sx={{
                                            width: {
                                                xs: "50%",
                                                sm: 82,
                                            },

                                            minWidth: {
                                                xs: 0,
                                                sm: 82,
                                            },

                                            height: 36,

                                            px: 1,

                                            borderRadius: "7px",

                                            fontSize: "12px",

                                            fontWeight: 600,

                                            textTransform: "none",

                                            color: "#ffffff",

                                            background:
                                                "linear-gradient(135deg, #2563eb, #1d4ed8)",

                                            boxShadow:
                                                "0 2px 5px rgba(37, 99, 235, 0.20)",

                                            transition:
                                                "all 0.2s ease",

                                            "&:hover": {
                                                background:
                                                    "linear-gradient(135deg, #1d4ed8, #1e40af)",

                                                boxShadow:
                                                    "0 4px 9px rgba(37, 99, 235, 0.25)",

                                                transform:
                                                    "translateY(-1px)",
                                            },

                                            "&:active": {
                                                transform:
                                                    "translateY(0)",
                                            },

                                            "&.Mui-disabled": {
                                                background:
                                                    "#93c5fd",

                                                color: "#ffffff",
                                            },
                                        }}
                                    >
                                        {isPending
                                            ? "Loading"
                                            : "SHOW"}
                                    </Button>

                                    {/* ==========================
                RESET BUTTON
            =========================== */}

                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleReset}
                                        startIcon={
                                            <FontAwesomeIcon
                                                icon={faRefresh}
                                                style={{
                                                    fontSize:
                                                        "11px",
                                                }}
                                            />
                                        }
                                        sx={{
                                            width: {
                                                xs: "50%",
                                                sm: 82,
                                            },

                                            minWidth: {
                                                xs: 0,
                                                sm: 82,
                                            },

                                            height: 36,

                                            px: 1,

                                            borderRadius: "7px",

                                            fontSize: "12px",

                                            fontWeight: 600,

                                            textTransform: "none",

                                            textTransform: "none",

                                        backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", color: "#2563eb",

                                        boxShadow:
                                            "0 1px 2px rgba(15, 23, 42, 0.05)",

                                        transition: "all 0.2s ease",

                                        "&:hover": {
                                            backgroundColor: "#dbeafe", borderColor: "#93c5fd", color: "#1d4ed8",
                                            boxShadow:
                                                "0 2px 5px rgba(15, 23, 42, 0.10)",

                                            transform: "translateY(-1px)",
                                        },

                                        "&:active": {
                                            backgroundColor: "#cbd5e1",

                                            transform: "translateY(0)",
                                        },
                                        }}
                                    >
                                        RESET
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>


                    </form>
                </div>



                {/* Error Message */}


                {/* Data Table Section */}
                <Paper sx={{
                    p: isMobile ? 1 : 1,
                    borderRadius: 3,
                    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
                    backgroundColor: 'background.paper'
                }} ref={tableRef}>
                    {/* Table Header */}


                    {/* Data Table */}
                    {reportData.length > 0 ? (
                        <Box sx={{ width: "100%", overflowX: "auto" }}>
                            <MUIDataTable
                                data={filteredAndGroupedData}
                                dataColumns={columns}
                                reportTitle={`${filters.Vou_Type === 'R' ? 'Receive' : 'Payment'} Register Report`}
                                orderGroup={groupBy}
                                currency={"all"}
                                filterDate={filters}
                                tradeLabel={`Type: ${filters.Vou_Type === 'R' ? 'Receive' : 'Payment'} `}
                                companyName={compName.join(",")}
                                hideIfZeroKeys={[]}
                                exportColumnKeys={exportColumnKeys}
                                enableHeaderFilter={true}

                            />
                        </Box>
                    ) : (
                        !isPending && (
                            <NoDataCard />
                        )
                    )}
                </Paper>


            </Box>
        </MainContainer>
    );
};

export default ReceivePaymentReport;
